import {
  getExternalActionTypesUrl,
  getExternalActionsForContextUrl,
  getExternalActionsUrl,
  getExternalAssessmentPlansUrl,
  getExternalAssessmentResultsUrl,
  getExternalAssessmentURl,
  getExternalAssessmentsStudentsEvidenceUrl,
  getExternalAssignmentTypesUrl,
  getExternalAssignmentUrl,
  getExternalAvgScores,
  getExternalContentVersionHistoryUrl,
  getExternalCyclesUrl,
  getExternalDashboardResultsUrl,
  getExternalDownloadUrl,
  getExternalFeatureFlagsUrl,
  getExternalFileContentUrl,
  getExternalFileUrl,
  getExternalFindingsUrl,
  getExternalInsightsTerms,
  getExternalMasteryLevelUrl,
  getExternalMasteryMapUrl,
  getExternalOutcomesByIdsUrl,
  getExternalPatchAssessmentUrl,
  getExternalPurlDataUrl,
  getExternalPurlUrl,
  getExternalRecommendationsUrl,
  getExternalReflectionsUrl,
  getExternalResultsUrl,
  getExternalRosterUrl,
  getExternalScoresUrl,
  getExternalShareCodeUrl,
  getExternalShareUrl,
  getExternalStructuredQuestionsUrl,
  getExternalSubjectsByIdsUrl,
  getExternalTasksUrl,
  getExternalTerms,
  getExternalUsersUrl,
  getPublicResultsUrl,
  getVerifyUrl,
  updateExternalQualitativeMeasureUrl,
} from "../config/Config";
import downloadFile from "../utils/Dom";
import { zipFiles } from "../utils/FileUtils";
import {
  deleteRequest,
  getRequest,
  patchRequest,
  postRequest,
  putRequest,
} from "./ServiceHelper";

export class ExternalService {
  static verifyEmail = (shareCode, email) => {
    const param = { code: shareCode, email: email };
    return postRequest(getVerifyUrl(), param);
  };

  static getAvgJaScores = (assignmentId) => {
    return getRequest(getExternalAvgScores(assignmentId));
  };

  static getExternalScoresByAssessor = (assignmentId, assessorId) => {
    const params = {};
    if (!assessorId) {
      params.assessorId = "external";
    } else {
      params.assessorId = assessorId;
    }
    return getRequest(getExternalScoresUrl(assignmentId), params);
  };

  static getShareCode = (shareCodeId) => {
    return getRequest(getExternalShareCodeUrl(shareCodeId));
  };

  static verifyUser = (userData) => {
    return postRequest(getVerifyUrl(), userData);
  };

  static getPurlDataById = (purlId) => {
    return getRequest(getExternalPurlDataUrl(purlId));
  };

  static getRatingToMasteryLevelMapsById = (mapId) => {
    return getRequest(getExternalMasteryMapUrl(mapId));
  };

  static getMasteryLevels = () => {
    return getRequest(getExternalMasteryLevelUrl());
  };

  static getAssignmentById = (id) => {
    return getRequest(getExternalAssignmentUrl(id));
  };

  static submitPurlResults = (purlResults) => {
    return postRequest(getExternalPurlUrl(), purlResults);
  };

  static getAssessmentForPurl = (assessmentId, termId) => {
    return getRequest(
      getExternalAssessmentURl() + "/" + assessmentId + "/" + termId
    );
  };

  static getAssessmentStudentsEvidence = (assessmentId, termIds) => {
    const params = { termIds: termIds };
    return getRequest(
      getExternalAssessmentsStudentsEvidenceUrl(assessmentId),
      params
    );
  };

  static getAssessmentPlanById = (planId) => {
    return getRequest(getExternalAssessmentPlansUrl() + "/" + planId);
  };

  static patchAssessment = (assessmentId, fields, approverEmail) => {
    const params = { approverEmail: approverEmail };
    return patchRequest(
      getExternalPatchAssessmentUrl(assessmentId),
      fields,
      true,
      true,
      false,
      null,
      params
    );
  };

  static getAssignmentsByPlanId = (planId) => {
    return getRequest(
      getExternalAssessmentPlansUrl() + "/" + planId + "/assignments"
    );
  };

  static getAssignmentTypes = () => {
    return getRequest(getExternalAssignmentTypesUrl());
  };

  static createTask = (obj) => {
    return postRequest(getExternalTasksUrl(), obj);
  };

  static getTaskById = (taskId) => {
    return getRequest(getExternalTasksUrl() + "/" + taskId);
  };

  static getAssessmentResults = (
    assignmentIds,
    mode,
    masteryResults,
    context,
    academic_subject_code,
    termIds,
    outcomeIds,
    finalized = false,
    expectedAssessorCount
  ) => {
    const params = { mode: mode };
    if (assignmentIds) {
      params.assignmentIds = assignmentIds;
    }
    if (masteryResults === "true") {
      params.masteryResults = masteryResults;
    }
    if (context) {
      params.contextType = context;
    }
    if (academic_subject_code) {
      params.academicSubject = academic_subject_code;
    }
    if (termIds) {
      params.termIds = termIds;
    }
    if (outcomeIds) {
      params.outcomeIds = outcomeIds;
    }
    if (finalized) {
      params.finalized = finalized;
    }
    if (expectedAssessorCount !== null && expectedAssessorCount !== undefined) {
      params.expectedAssessorCount = expectedAssessorCount;
    }
    return getRequest(getExternalAssessmentResultsUrl(), params);
  };

  /**
   * Returns pre-signed URL and corresponding fields to upload to S3
   * @param fileName
   * @param type
   * @return {Promise<*>}
   */
  static getPreSignedPostUrl = (
    fileName,
    type,
    path = null,
    metadata = null
  ) => {
    const params = {};
    params.type = type;
    if (path) {
      params.path = path;
    }
    if (metadata) {
      params.metadata = JSON.stringify(metadata);
    }
    return getRequest(getExternalFileUrl() + "/" + fileName, params);
  };

  static async saveFilesIndividually(files, type, path = null) {
    await Promise.all(
      files.map((file) => ExternalService.saveFile(file, type, path))
    );
  }

  // Replace eventually, as we want control over zipping files or not
  static async saveFiles(files, type, path = null, fileName = null) {
    let file;
    if (Array.isArray(files) && files.length > 1) {
      file = await zipFiles(files);
      file = new File([file], fileName, { type: "application/zip" });
    } else {
      file = files[0];
    }
    ExternalService.saveFile(file, type, path);
  }

  static async saveFile(file, type, path = null, metadata = null) {
    const preSignedUrl = await this.getPreSignedPostUrl(
      file.name,
      type,
      path,
      metadata
    );
    const formData = new FormData();
    for (const [key, value] of Object.entries(preSignedUrl.fields)) {
      formData.append(key, value);
    }
    formData.append("File", file);
    return postRequest(preSignedUrl.url, formData, false, false, true);
  }

  static deleteFile(fileName, type, path = null, metadata = null) {
    const params = { type };
    if (path) {
      params.path = path;
    }
    if (metadata) {
      params.metadata = JSON.stringify(metadata);
    }
    const queryString = new URLSearchParams(params).toString();
    return deleteRequest(
      getExternalFileUrl() + "/" + fileName + "?" + queryString
    );
  }

  static getFileContent(fileName, type, path) {
    return getRequest(getExternalFileContentUrl() + "/" + fileName, {
      type: type,
      path: path,
    });
  }

  static async reviewFiles(type, path = null, filter = null) {
    const paths = await this.requestDownload(type, path, filter);
    return paths;
  }

  /**
   * Downloads all files in a given path
   * @param {string} type  A Type conforming to DDL File Types
   * @param {string} path Any dirs following the type
   */
  static downloadFiles = async (type, path, listOnly = false) => {
    const paths = await this.requestDownload(type, path);
    if (listOnly) {
      return paths;
    } else {
      paths.forEach((element) => {
        downloadFile(element.file_url, element.file_name);
      });
    }
  };

  /**
   * Requests a list pre-signed URLs to download a collection of files from S3
   * @param {string} type A Type conforming to DDL File Types
   * @param {string} path Any dirs following the type
   * @returns
   */
  static requestDownload = (type, path, filter = null) => {
    let params = {};
    params.type = type;
    if (path) {
      params.path = path;
    }
    if (filter) {
      params = { ...params, ...filter };
    }
    return getRequest(getExternalDownloadUrl(), params);
  };

  static getRecs = async () => {
    return await getRequest(getExternalRecommendationsUrl());
  };

  static createRec = async (rec) => {
    return await postRequest(getExternalRecommendationsUrl(), rec);
  };

  static updateRec = async (rec) => {
    return await putRequest(getExternalRecommendationsUrl(), rec);
  };

  static getFindings = async () => {
    const findings = await getRequest(getExternalFindingsUrl());
    return findings;
  };

  static createFinding = async (finding) => {
    const newFinding = await postRequest(getExternalFindingsUrl(), finding);
    return newFinding;
  };

  static updateFinding = async (id, finding) => {
    const updatedFinding = await putRequest(
      getExternalFindingsUrl() + "/" + id,
      finding
    );
    return updatedFinding;
  };

  static updateMeasure = (measure) => {
    const measureId = measure.qualitativeMeasure.id
      ? measure.qualitativeMeasure.id
      : measure.qualitativeMeasure.ddl_id;
    return putRequest(updateExternalQualitativeMeasureUrl(measureId), measure);
  };

  static approveAssessmentPlanResults = (planId, note) => {
    const params = { contextId: planId, note: note };
    return postRequest(getExternalAssessmentPlansUrl() + "/approval", params);
  };

  static approveSectionResults = (sectionId, note) => {
    const params = { contextId: sectionId, note: note };
    return postRequest(
      getExternalAssessmentPlansUrl() + "/section/approval",
      params
    );
  };

  static getApprovalNotesByPlanId = (planId) => {
    return getRequest(
      getExternalAssessmentPlansUrl() + "/approval/notes/" + planId
    );
  };

  static updateApprovalNote = (noteId, newNote) => {
    const params = { note: newNote };
    return putRequest(
      getExternalAssessmentPlansUrl() + "/approval/" + noteId,
      params
    );
  };

  static getExternalRosterByContext(contextId) {
    return getRequest(getExternalRosterUrl(contextId));
  }

  static addExternalUserToOffering(canvasId, contextId) {
    const params = {};
    params.studentCanvasId = canvasId;
    params.contextId = contextId;
    return putRequest(getExternalUsersUrl() + "/add/offering", params);
  }
  static removeExternalUserFromOffering(studentId, contextId) {
    const params = {};
    params.contextId = contextId;
    return putRequest(getExternalUsersUrl() + "/remove/" + studentId, params);
  }

  static getExternalReflectionsForContext = (contextId) => {
    return getRequest(getExternalReflectionsUrl() + "/" + contextId);
  };

  static getExternalActionsForContext = (contextId) => {
    return getRequest(getExternalActionsForContextUrl() + "/" + contextId);
  };

  static addExternalReflectionForContext = ({
    reflectionId,
    sectionId,
    templateId,
    contextId,
    author,
    actions = [],
    userId = null,
  }) => {
    const param = {
      reflectionId: reflectionId,
      author: author,
      actions: actions,
    };
    if (userId) {
      param.userId = userId;
    }
    if (sectionId) {
      param.sectionId = sectionId;
    }
    if (templateId) {
      param.templateId = templateId;
    }

    return postRequest(getExternalReflectionsUrl() + "/" + contextId, param);
  };

  static getExternalActionTypes = () => {
    return getRequest(getExternalActionTypesUrl()).then((actionTypes) => {
      return actionTypes;
    });
  };

  static updateExternalAction = (action) => {
    return putRequest(getExternalActionsUrl() + "/" + action.id, action);
  };

  static createExternalActionForContext = (newAction, contextId) => {
    return postRequest(
      getExternalActionsForContextUrl() + "/" + contextId,
      newAction
    );
  };

  static updateExternalActionType = (actionTypeId, name) => {
    const param = { id: actionTypeId, name: name };
    return putRequest(getExternalActionTypesUrl() + "/" + actionTypeId, param);
  };

  static createExternalActionType = (name) => {
    const param = { name: name };
    return postRequest(getExternalActionTypesUrl(), param);
  };

  static deleteExternalReflection = (contextId, reflectionId) => {
    return deleteRequest(getExternalReflectionsUrl() + "/delete/" + contextId, {
      reflectionId: reflectionId,
    });
  };

  static updateExternalReflection = (reflection) => {
    return putRequest(
      getExternalReflectionsUrl() + "/update/" + reflection.id,
      reflection
    );
  };

  static deleteExternalAction = (actionId) => {
    return deleteRequest(getExternalActionsUrl() + "/" + actionId);
  };

  static publishExternalResults = (planId, email, subjectMap) => {
    const params = {};
    if (planId) {
      params.plan_id = planId;
      params.userEmail = email;
      params.subjectMap = subjectMap;
    }
    return postRequest(getExternalResultsUrl() + "/public", params);
  };

  static getPublicShareCode = (shareCode, tenantId) => {
    return getRequest(
      getExternalShareUrl() + "/public/" + shareCode + "/" + tenantId
    );
  };

  static getPublicResultsByPlanId = (planId, tenantId) => {
    return getRequest(
      getPublicResultsUrl() + "/public/" + planId + "/" + tenantId
    );
  };

  static getTerms = () => {
    return getRequest(getExternalTerms());
  };

  static getInsightsTerms = () => {
    return getRequest(getExternalInsightsTerms());
  };

  static getCycles = () => {
    return getRequest(getExternalCyclesUrl());
  };

  static updateExternalDatalink = (datalink, dataLinkId) => {
    return putRequest(
      getExternalAssessmentPlansUrl() + "/datalink/" + dataLinkId,
      datalink
    );
  };

  static getExternalSubjectsByIds = (subjectIds) => {
    const params = { academicSubjectIds: subjectIds };
    return getRequest(getExternalSubjectsByIdsUrl(), params);
  };

  static getDashboardResults = (
    LOType,
    outcomeIds = [],
    subject,
    mode,
    terms = [],
    courseLevels = [],
    masteryResults = null,
    categories = [],
    context,
    courseIds = [],
    variety = null,
    offeringIds = [],
    finalized = false
  ) => {
    const params = { outcomeType: LOType, mode: mode };
    if (subject) {
      params.academicSubject = subject;
    }
    if (outcomeIds?.length > 0) {
      params.outcomeIds = outcomeIds;
    }
    if (variety) {
      params.variety = variety;
    }
    if (terms?.length > 0) {
      const intTerms = []; // term ids should be strings for selector but ints for backend
      terms.forEach((term) => intTerms.push(parseInt(term)));
      params.termIds = intTerms;
    }
    if (courseLevels?.length > 0) {
      params.courseLevels = courseLevels;
    }
    if (masteryResults === "true") {
      params.masteryResults = masteryResults;
    }
    if (categories?.length > 0) {
      params.categoryIds = categories;
    }
    if (context) {
      params.contextType = context;
    }
    if (courseIds?.length > 0) {
      params.courseIds = courseIds;
    }
    if (offeringIds?.length > 0) {
      params.offeringIds = offeringIds;
    }
    if (finalized) {
      params.finalized = finalized;
    }

    return getRequest(getExternalDashboardResultsUrl(), params);
  };

  static getLearningOutcomesByIds = (outcomeIds, enrichRatings = false) => {
    const params = { outcomeIds: outcomeIds, enrichRatings: enrichRatings };

    return getRequest(getExternalOutcomesByIdsUrl(), params);
  };

  static getStructuredQuestions = (contextId) => {
    return getRequest(getExternalStructuredQuestionsUrl(contextId));
  };

  static getContentVersionHistoryExternal = (fileType, contextPath, email) => {
    return getRequest(getExternalContentVersionHistoryUrl(fileType), {
      contextPath,
      email,
    });
  };

  static getFeatureFlags = () => {
    return getRequest(getExternalFeatureFlagsUrl());
  };
}
